import React, { useEffect, useState } from "react";
import { FaPencilAlt, FaTimes, FaCamera, FaImage } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getPrefixes, usersResource } from "../../helpers/configRoutes/backend_helper";
import CustomInput from "../../components/form/CustomInput";
import { useFormik } from "formik"
import CustomSelect from "../../components/form/CustomSelect";
import FileInput from "../../components/form/FileInput";
import * as Yup from "yup";
import { validatePhones } from "../../helpers/forms/Validation";
import FloatingActionButton from "../../components/elements/FloatingActionButton";
import CameraPhotoModal from "../../components/modal/CameraPhotoModal";
import { updateUser } from "../../store/auth/login/action";

const EditProfile = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const user = useSelector((state) => state.Login.user)
    const [loading, setLoading] = useState(false)
    const [prefixes, setPrefixes] = useState([])
    const [showOptions, setShowOptions] = useState(false);
    const [modal, setModal] = useState({ show: false });

    const validation = useFormik({
        initialValues: user,
        validationSchema: Yup.object({
            email: Yup.string().email('Invalid email').required('Email is required'),
            first_name: Yup.string().required('First name is required'),
            last_name: Yup.string().required('Last name is required'),
            phone: validatePhones(true),
            prefix_id: Yup.string().required('Prefix is required'),
            driver_license: Yup.string().required('Driver license is required'),
            license_expiration: Yup.date()
                .min(new Date(), 'License expiration must be a future date')
                .required('License expiration is required'),
            dot_physical_certificate: Yup.string().required('DOT Physical certificate is required'),
            dot_physical_expiration: Yup.date()
                .min(new Date(), 'DOT Physical expiration must be a future date')
                .required('DOT Physical expiration is required'),
        }),
        onSubmit: async (values, { setErrors }) => {
            setLoading(true);
            const response = await usersResource('put', values, values.id);
            if (response.status) {
                dispatch(updateUser(response.user || values))
                navigate('/profile')
            } else {
                setErrors(response.errors || {})
            }
            setLoading(false);
        }
    });

    useEffect(() => {
        const getPrefixesCatalog = async () => {
            const response = await getPrefixes();
            if (response.status) {
                setPrefixes(response.prefixes);
            }
        }
        getPrefixesCatalog();
    }, []);

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center p-4">
            {/* Sección de Encabezado */}
            <h1 className="text-lg font-semibold text-gray-800 mb-6">Profile</h1>

            {/* Imagen de Perfil */}
            <div className="relative mb-4">
                <div className="w-24 h-24 rounded-full bg-mts-primary flex items-center justify-center">
                    {user.imageProfile ?
                        <img
                            src={validation.values.imageProfile} // Reemplaza con la URL o variable de tu imagen
                            alt="User Profile"
                            className="w-24 h-24 rounded-full object-cover border-2 border-white shadow-md"
                        />
                        :
                        <span className="text-4xl text-mts-primary-hover">👤</span>
                    }
                </div>
                {/* Botón de edición */}
                <button onClick={() => setShowOptions(true)} className="absolute bottom-1 right-1 bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center shadow-md">
                    <FaPencilAlt size={10} />
                </button>
                {showOptions && (
                    <div className="absolute left-20 bg-white shadow-lg rounded-lg p-2 flex flex-col gap-2">
                        <button
                            onClick={() => setModal({ show: true })}
                            className="flex items-center gap-2 text-gray-700 hover:text-blue-500 p-2"
                        >
                            <FaCamera /> Take a picture
                        </button>
                        <FileInput
                            validation={validation}
                            format={'img'}
                            name={'imageProfile'}
                        >
                            <FaImage /> Select an image
                        </FileInput>
                        <button
                            onClick={() => setShowOptions(false)}
                            className="flex items-center gap-2 text-red-500 hover:text-red-700 p-2"
                        >
                            <FaTimes /> Cancel
                        </button>
                    </div>
                )}
            </div>

            {/* Nombre y Correo */}
            <div className="text-center mb-6">
                <h2 className="text-lg font-bold text-gray-800">{user.name}</h2>
                <p className="text-gray-500 text-sm">{user.email}</p>
            </div>


            {/* Opciones de Perfil */}
            <div className="w-full max-w-md bg-white rounded-lg shadow">
                <div
                    className="flex flex-col justify-between items-start py-4 px-6 border-b last:border-b-0 hover:bg-gray-100 cursor-pointer"
                >

                    <div className="flex flex-col gap-4 w-full">
                        <CustomInput
                            label="First name"
                            type="text"
                            name="first_name"
                            labelClassName="text-base"
                            error={validation?.errors?.first_name}
                            value={validation?.values?.first_name}
                            handleChange={validation.handleChange}
                        />
                        <CustomInput
                            label="Last name"
                            type="text"
                            name="last_name"
                            labelClassName="text-base"
                            error={validation?.errors?.last_name}
                            value={validation?.values?.last_name}
                            handleChange={validation.handleChange}
                        />
                    </div>
                </div>

                <div
                    className="justify-between items-center py-4 px-6 border-b last:border-b-0 hover:bg-gray-100 cursor-pointer"
                >
                    <div className="flex flex-col gap-4 w-full">
                        <CustomSelect
                            label="Phone Prefix"
                            options={prefixes}
                            name="prefix_id"
                            labelClassName={'text-base'}
                            error={validation?.errors?.prefix_id}
                            value={validation?.values?.prefix_id}
                            handleChange={validation.handleChange}
                        />
                        <CustomInput
                            label="Phone Number"
                            type="text"
                            name="phone"
                            labelClassName={'text-base'}
                            error={validation?.errors?.phone}
                            value={validation?.values?.phone}
                            handleChange={validation.handleChange}
                        />
                    </div>
                </div>
                <div
                    className=" justify-between items-center py-4 px-6 border-b last:border-b-0 hover:bg-gray-100 cursor-pointer"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <CustomInput
                            label="Driver License Expiration"
                            type="date"
                            name="license_expiration"
                            labelClassName={'text-base'}
                            error={validation?.errors?.license_expiration}
                            value={validation?.values?.license_expiration}
                            handleChange={validation.handleChange}
                        />
                        <FileInput
                            className={'bg-mts-gray-button'}
                            validation={validation}
                            format={'all'}
                            name={'driver_license'}
                            nameInput={'Driver License (PDF or image)'}
                            maxSizeMb={5}
                        />
                    </div>
                </div>
                <div
                    className=" justify-between items-center py-4 px-6 border-b last:border-b-0 hover:bg-gray-100 cursor-pointer"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <CustomInput
                            label="DOT Physical Expiration"
                            type="date"
                            name="dot_physical_expiration"
                            labelClassName={'text-base'}
                            error={validation?.errors?.dot_physical_expiration}
                            value={validation?.values?.dot_physical_expiration}
                            handleChange={validation.handleChange}
                        />
                        <FileInput
                            className={'bg-mts-gray-button'}
                            validation={validation}
                            format={'all'}
                            name={'dot_physical_certificate'}
                            nameInput={'DOT Physical (PDF or image)'}
                            maxSizeMb={5}
                        />
                    </div>
                </div>
                <FloatingActionButton
                    onSave={validation.handleSubmit}
                    onCancel={() => navigate('/profile')}
                    loading={loading}
                />
                {modal.show &&
                    <CameraPhotoModal modal={modal} setModal={setModal} validation={validation} />
                }
            </div>
        </div>
    );
};

export default EditProfile;
